import { useMemo, useRef, useState } from 'react';
import { Copy, RefreshCw, Sparkles, X } from 'lucide-react';
import type {
  AIAssistanceResponseSchema,
  AIInsightResponse,
  AIOperation,
} from '@/domain/ai/contracts';
import type { z } from 'zod';
import {
  buildBenchmarkInterpretationContext,
  buildBottleneckExplanationContext,
  buildDiagnosisExplanationContext,
  buildFunnelExplanationContext,
  buildImprovementContext,
  buildProjectionExplanationContext,
} from '@/domain/ai/context';
import { BenchmarkRecordSchema } from '@/domain/diagnosis/benchmarks';
import { apiPost } from '@/infrastructure/api-client';
import { localizeSystemValue } from '@/app/localization';
import { useEditorStore } from '@/features/funnel-editor/state/editor-store';
import { useDiagnosis } from '@/features/funnel-editor/diagnosis/use-diagnosis';
import { useSimulation } from '@/features/funnel-editor/simulation/use-simulation';
import type { DiagnosisFinding } from '@/domain/diagnosis/contracts';

type AIAssistanceResponse = z.infer<typeof AIAssistanceResponseSchema>;
type Scope = 'entire' | 'selected_node' | 'selected_branch' | 'finding' | 'scenario';
type BenchmarkMetric = 'roi' | 'roas' | 'cpl' | 'cac' | 'leads' | 'projectedClients';

const OPERATION_LABELS: Array<{ operation: AIOperation; label: string }> = [
  { operation: 'explain_funnel', label: 'Resumir' },
  { operation: 'diagnose_funnel', label: 'Explicar diagnóstico' },
  { operation: 'suggest_improvements', label: 'Sugerir melhorias' },
  { operation: 'generate_hypotheses', label: 'Gerar hipóteses' },
  { operation: 'explain_projection', label: 'Explicar cenário' },
  { operation: 'explain_bottlenecks', label: 'Explicar gargalo' },
];

function metricProjection(metric: { status: string; effectiveValue: unknown }) {
  return { status: metric.status, value: metric.effectiveValue };
}

function SafeAIText({ children }: { children: string }) {
  return <p className="whitespace-pre-wrap text-sm text-text-secondary">{children}</p>;
}

export function AIResponseView({ response }: { response: AIInsightResponse }) {
  return (
    <section aria-label="Resposta da IA" className="space-y-3">
      <SafeAIText>{response.summary}</SafeAIText>
      {response.insights.map((insight) => (
        <article key={insight.title} className="rounded border border-border-subtle p-3">
          <h3 className="text-sm font-medium text-text-primary">{insight.title}</h3>
          <SafeAIText>{insight.summary}</SafeAIText>
          <p className="mt-1 text-xs text-text-muted">Confiança: {localizeSystemValue(insight.confidence)}</p>
          {insight.caveats.map((caveat) => (
            <p key={caveat} className="mt-1 text-xs text-text-muted">
              Ressalva: {caveat}
            </p>
          ))}
        </article>
      ))}
      {response.recommendations.map((recommendation) => (
        <article
          key={recommendation.title}
          className="rounded border border-border-brand bg-[rgba(198,144,50,0.06)] p-3"
        >
          <h3 className="text-sm font-medium text-text-primary">{recommendation.title}</h3>
          <SafeAIText>{recommendation.rationale}</SafeAIText>
          <p className="mt-1 text-xs text-text-muted">
            Alvo: {recommendation.target.artifactType} {recommendation.target.artifactId} ·
            Confiança: {localizeSystemValue(recommendation.confidence)}
          </p>
        </article>
      ))}
      {response.hypotheses.map((hypothesis) => (
        <article key={hypothesis.statement} className="rounded border border-border-subtle p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-text-brand">
            Hipótese a testar
          </h3>
          <SafeAIText>{hypothesis.statement}</SafeAIText>
          <p className="mt-1 text-xs text-text-muted">Métrica: {hypothesis.measurement}</p>
          <p className="text-xs text-text-muted">Sinal de sucesso: {hypothesis.successSignal}</p>
        </article>
      ))}
      {response.suggestedChanges.map((change) => (
        <article
          key={change.id}
          aria-label={`Alteração sugerida: ${change.title}`}
          className="rounded border border-border-subtle p-3"
        >
          <h3 className="text-sm font-medium">{change.title}</h3>
          <SafeAIText>{change.rationale}</SafeAIText>
          <p className="mt-1 text-xs text-text-muted">
            Apenas proposta · revise manualmente · sem aplicação automática
          </p>
        </article>
      ))}
      {response.caveats.map((caveat) => (
        <p key={caveat} className="text-xs text-text-muted">
          Ressalva: {caveat}
        </p>
      ))}
      {response.confidenceNote ? (
        <p className="text-xs text-text-muted">Observação de confiança: {response.confidenceNote}</p>
      ) : null}
    </section>
  );
}

export function AIAssistantPanel({
  funnelId,
  funnelName,
  description,
  initialFindingId,
  onClose,
}: {
  funnelId: string;
  funnelName: string;
  description?: string | null;
  initialFindingId?: string;
  onClose: () => void;
}) {
  const graph = useEditorStore((state) => state.graph);
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const createStrategicNote = useEditorStore((state) => state.createStrategicNote);
  const diagnosis = useDiagnosis();
  const simulation = useSimulation();
  const [scope, setScope] = useState<Scope>(initialFindingId ? 'finding' : 'entire');
  const [findingId, setFindingId] = useState(
    initialFindingId ?? diagnosis?.result.items[0]?.id ?? '',
  );
  const [response, setResponse] = useState<AIInsightResponse | null>(null);
  const [lastOperation, setLastOperation] = useState<AIOperation>('explain_funnel');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [benchmarkMetric, setBenchmarkMetric] = useState<BenchmarkMetric>('roi');
  const [benchmarkUnit, setBenchmarkUnit] = useState('ratio');
  const [benchmarkValue, setBenchmarkValue] = useState('');
  const [benchmarkSource, setBenchmarkSource] = useState('');
  const [benchmarkCategory, setBenchmarkCategory] = useState('funnel');
  const controller = useRef<AbortController | null>(null);
  const finding = diagnosis?.result.items.find((item) => item.id === findingId);
  const suppliedBenchmark = BenchmarkRecordSchema.safeParse({
    id: 'user-supplied-benchmark',
    metric: benchmarkMetric,
    unit: benchmarkUnit.trim(),
    value: benchmarkValue.trim() === '' ? undefined : Number(benchmarkValue),
    source: { name: benchmarkSource.trim() },
    context: { category: benchmarkCategory.trim() },
    confidence: 0.5,
  });
  const visibleNodeIds = useMemo(() => {
    if (!graph || scope === 'entire' || scope === 'finding' || scope === 'scenario')
      return graph?.nodes.map((node) => node.id) ?? [];
    if (scope === 'selected_node') return selectedIds;
    const branch = new Set(selectedIds);
    for (const edge of graph.edges)
      if (branch.has(edge.source) || branch.has(edge.target)) {
        branch.add(edge.source);
        branch.add(edge.target);
      }
    return [...branch];
  }, [graph, scope, selectedIds]);

  function graphSummary() {
    if (!graph) return { nodes: [], edges: [] };
    const allowed = new Set(visibleNodeIds);
    return {
      nodes: graph.nodes
        .filter((node) => allowed.has(node.id))
        .map((node) => ({
          id: node.id,
          type: node.type ?? 'unknown',
          name: String(node.data.name ?? node.data.label ?? 'Sem título'),
          status: String(node.data.status ?? 'active'),
          metricStates:
            node.data.metrics && typeof node.data.metrics === 'object'
              ? (node.data.metrics as Record<string, unknown>)
              : undefined,
        })),
      edges: graph.edges
        .filter((edge) => allowed.has(edge.source) && allowed.has(edge.target))
        .map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          type: edge.type ?? 'standard',
        })),
    };
  }

  function buildRequest(operation: AIOperation) {
    if (!graph || !diagnosis || !simulation) throw new Error('O contexto determinístico não está pronto.');
    const budget = { maxBytes: 32_000 };
    let built;
    if (operation === 'interpret_benchmark') {
      if (!suppliedBenchmark.success) throw new Error('Primeiro informe um benchmark válido com fonte.');
      const actual = simulation.active.funnel[benchmarkMetric];
      built = buildBenchmarkInterpretationContext({
        funnelId,
        benchmark: {
          status: 'matched',
          matches: [suppliedBenchmark.data],
          missingContext: [],
          explanation: 'A user-supplied sourced benchmark matches the selected metric and unit.',
        },
        actualMetric: metricProjection(actual),
        budget,
      });
    } else if (operation === 'diagnose_funnel' || operation === 'explain_bottlenecks') {
      const selectedFinding: DiagnosisFinding | undefined =
        operation === 'explain_bottlenecks'
          ? (diagnosis.result.items.find(
              (item) => item.id === findingId && item.type === 'bottleneck',
            ) ?? diagnosis.result.items.find((item) => item.type === 'bottleneck'))
          : (finding ?? diagnosis.result.items[0]);
      if (!selectedFinding)
        throw new Error(
          `No deterministic ${operation === 'explain_bottlenecks' ? 'bottleneck' : 'finding'} is available.`,
        );
      built =
        operation === 'explain_bottlenecks'
          ? buildBottleneckExplanationContext({ funnelId, finding: selectedFinding, budget })
          : buildDiagnosisExplanationContext({ funnelId, finding: selectedFinding, budget });
    } else if (operation === 'suggest_improvements') {
      built = buildImprovementContext({
        funnelId,
        selectedNodeIds: visibleNodeIds,
        scores: diagnosis.report.scores,
        priorities: diagnosis.report.actionPlan,
        metrics: simulation.active.funnel as unknown as Record<string, unknown>,
        budget,
      });
    } else if (operation === 'explain_projection') {
      const active = simulation.active.funnel;
      built = buildProjectionExplanationContext({
        funnelId,
        projection: {
          scenarioId: simulation.activeScenario?.id ?? 'base',
          scenarioName: simulation.activeScenario?.name ?? 'Base',
          status: active.warnings.length ? 'partial' : 'known',
          outputs: {
            leads: metricProjection(active.leads),
            clients: metricProjection(active.projectedClients),
            revenue: metricProjection(active.projectedRevenue),
            roi: metricProjection(active.roi),
            roas: metricProjection(active.roas),
          },
          deltas: {},
          warnings: active.warnings.map((warning) => warning.message),
        },
        budget,
      });
    } else {
      built = buildFunnelExplanationContext({
        funnel: {
          id: funnelId,
          name: funnelName,
          ...(description ? { description } : {}),
          nodeCount: graph.nodes.length,
          edgeCount: graph.edges.length,
        },
        graph: graphSummary(),
        scores: diagnosis.report.scores,
        diagnosisSummary: diagnosis.report.evidenceSummary,
        selectedNodeIds: visibleNodeIds,
        budget,
      });
    }
    return {
      operation,
      context: built.context,
      sourceArtifacts: [
        ...built.sourceArtifacts,
        ...visibleNodeIds.map((id) => ({ artifactType: 'node', artifactId: id })),
      ],
    };
  }

  async function run(operation: AIOperation) {
    controller.current?.abort();
    const nextController = new AbortController();
    controller.current = nextController;
    setLoading(true);
    setError(null);
    setLastOperation(operation);
    try {
      const result = await apiPost<AIAssistanceResponse>(
        `/api/funnels/${funnelId}/ai`,
        buildRequest(operation),
        nextController.signal,
      );
      setResponse(result.result);
      setNoteDraft(result.result.summary);
    } catch (caught) {
      if (!nextController.signal.aborted)
        setError(caught instanceof Error ? caught.message : 'A assistência de IA falhou.');
    } finally {
      if (controller.current === nextController) {
        setLoading(false);
        controller.current = null;
      }
    }
  }

  async function saveAsNote() {
    if (!noteDraft.trim() || !window.confirm('Salvar este rascunho de IA revisado como observação estratégica?'))
      return;
    createStrategicNote({
      title: 'Observação assistida por IA',
      body: noteDraft.trim(),
      nodeIds: selectedIds,
    });
    await apiPost(`/api/funnels/${funnelId}/ai/events`, { action: 'saved_as_note' }).catch(
      () => undefined,
    );
  }

  return (
    <aside
      aria-label="Assistente de IA"
      className="w-[440px] shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-4"
    >
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold">
          <Sparkles size={17} /> Assistente de IA
        </h2>
        <button
          type="button"
          aria-label="Fechar assistente de IA"
          onClick={() => {
            controller.current?.abort();
            onClose();
          }}
        >
          <X size={17} />
        </button>
      </div>
      <p className="mt-1 text-xs text-text-muted">
        Apenas consultivo. As métricas e o diagnóstico determinísticos continuam canônicos; a IA nunca
        altera o grafo silenciosamente.
      </p>
      <label className="mt-3 block text-xs text-text-secondary">
        Escopo do contexto
        <select
          aria-label="Escopo do contexto da IA"
          className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5"
          value={scope}
          onChange={(event) => setScope(event.target.value as Scope)}
        >
          <option value="entire">Funil completo</option>
          <option value="selected_node" disabled={!selectedIds.length}>
            Nó selecionado
          </option>
          <option value="selected_branch" disabled={!selectedIds.length}>
            Ramificação selecionada
          </option>
          <option value="finding" disabled={!diagnosis?.result.items.length}>
            Achado do diagnóstico
          </option>
          <option value="scenario">Cenário ativo</option>
        </select>
      </label>
      {scope === 'finding' ? (
        <label className="mt-2 block text-xs text-text-secondary">
          Achado
          <select
            aria-label="Achado de diagnóstico da IA"
            className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5"
            value={findingId}
            onChange={(event) => setFindingId(event.target.value)}
          >
            {diagnosis?.result.items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {OPERATION_LABELS.map(({ operation, label }) => (
          <button
            key={operation}
            type="button"
            disabled={loading}
            onClick={() => void run(operation)}
            className="rounded border border-border-subtle px-2 py-1 text-xs hover:border-border-brand disabled:opacity-50"
          >
            {label}
          </button>
        ))}
      </div>
      <details className="mt-3 rounded border border-border-subtle p-2">
        <summary className="cursor-pointer text-xs font-medium text-text-secondary">
          Benchmark com fonte
        </summary>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <label className="text-xs text-text-secondary">
            Métrica
            <select
              aria-label="Métrica do benchmark"
              value={benchmarkMetric}
              onChange={(event) => setBenchmarkMetric(event.target.value as BenchmarkMetric)}
              className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5"
            >
              <option value="roi">ROI</option>
              <option value="roas">ROAS</option>
              <option value="cpl">CPL</option>
              <option value="cac">CAC</option>
              <option value="leads">Leads</option>
              <option value="projectedClients">Clientes projetados</option>
            </select>
          </label>
          <label className="text-xs text-text-secondary">
            Unidade
            <input aria-label="Unidade do benchmark" value={benchmarkUnit} onChange={(event) => setBenchmarkUnit(event.target.value)} className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5" />
          </label>
          <label className="text-xs text-text-secondary">
            Valor
            <input aria-label="Valor do benchmark" type="number" step="any" value={benchmarkValue} onChange={(event) => setBenchmarkValue(event.target.value)} className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5" />
          </label>
          <label className="text-xs text-text-secondary">
            Categoria
            <input aria-label="Categoria do benchmark" value={benchmarkCategory} onChange={(event) => setBenchmarkCategory(event.target.value)} className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5" />
          </label>
          <label className="col-span-2 text-xs text-text-secondary">
            Fonte
            <input aria-label="Fonte do benchmark" placeholder="Relatório ou editora" value={benchmarkSource} onChange={(event) => setBenchmarkSource(event.target.value)} className="mt-1 w-full rounded border border-border-subtle bg-surface-2 px-2 py-1.5" />
          </label>
        </div>
        <button
          type="button"
          disabled={loading || !suppliedBenchmark.success}
          title={suppliedBenchmark.success ? undefined : 'Valor, unidade, categoria e fonte são obrigatórios'}
          onClick={() => void run('interpret_benchmark')}
          className="mt-2 rounded border border-border-subtle px-2 py-1 text-xs hover:border-border-brand disabled:opacity-50"
        >
          Interpretar benchmark
        </button>
        <p className="mt-1 text-xs text-text-muted">O assistente interpreta apenas o benchmark informado por você; ele nunca inventa um.</p>
      </details>
      {loading ? (
        <p role="status" aria-live="polite" className="mt-4 text-sm">
          Gerando uma resposta fundamentada em evidências…
        </p>
      ) : null}
      {error ? (
        <div role="alert" className="mt-4 rounded border border-[rgba(226,85,85,0.45)] p-3 text-sm">
          <p>{error}</p>
          <button type="button" className="mt-2 underline" onClick={() => void run(lastOperation)}>
            Tentar novamente
          </button>
        </div>
      ) : null}
      {response ? (
        <div className="mt-4">
          <AIResponseView response={response} />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(response.summary)}
              className="flex items-center gap-1 rounded border border-border-subtle px-2 py-1 text-xs"
            >
              <Copy size={13} /> Copiar
            </button>
            <button
              type="button"
              onClick={() => void run(lastOperation)}
              className="flex items-center gap-1 rounded border border-border-subtle px-2 py-1 text-xs"
            >
              <RefreshCw size={13} /> Gerar novamente
            </button>
          </div>
          <label className="mt-3 block text-xs text-text-secondary">
            Rascunho de observação
            <textarea
              aria-label="Rascunho de observação da IA"
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              className="mt-1 min-h-24 w-full rounded border border-border-subtle bg-surface-2 p-2 text-sm"
            />
          </label>
          <button
            type="button"
            onClick={() => void saveAsNote()}
            className="mt-2 rounded border border-border-brand px-2 py-1 text-xs text-text-brand"
          >
            Revisar e salvar como observação
          </button>
        </div>
      ) : (
        <p className="mt-4 text-sm text-text-muted">
          Escolha um escopo e uma ação. Os resultados permanecem temporários até que você salve
          explicitamente uma observação revisada.
        </p>
      )}
    </aside>
  );
}
